import { useTranslation } from 'react-i18next';
import { Prohibit } from '@phosphor-icons/react';
import { useClientSelection } from '../contexts/ClientSelectionContext';
import SelectClientPrompt from './SelectClientPrompt';
import { AdminClient } from '../lib/api';

type ModuleKey = 'orders' | 'products' | 'replenishment' | 'rma' | 'warehousing';

const MODULE_FLAG: Record<ModuleKey, (c: AdminClient) => boolean> = {
  orders: (c) => c.ordersEnabled === true,
  products: (c) => c.productsEnabled === true,
  replenishment: (c) => c.replenishmentEnabled === true,
  rma: (c) => c.rmaEnabled === true,
  warehousing: (c) => c.warehousingEnabled === true,
};

/**
 * Route-level guard for client-scoped pages (admins only — regular users pass
 * straight through; their tenant and module access are enforced server-side).
 *
 * - No acting client selected in the header → "select a client" prompt.
 * - `module` given and the selected client doesn't have it enabled → notice.
 *   This replaces the per-module filtering the old page-local dropdowns did
 *   (they only listed clients with the module enabled; the global header
 *   dropdown lists all enabled clients).
 */
export default function ClientGate({
  module,
  children,
}: {
  module?: ModuleKey;
  children: React.ReactNode;
}) {
  const { isAdmin, effectiveClientId, selectedClient, selectedStoreModules, clients } =
    useClientSelection();

  if (!isAdmin) {
    // Multi-store users: the selected store may not have this module —
    // show the notice instead of a broken page. Single-store users pass
    // straight through (nav + backend already enforce their modules).
    if (module && selectedStoreModules && !selectedStoreModules.includes(module)) {
      return (
        <ModuleNotEnabledNotice
          clientName={clients.find((c) => c.id === effectiveClientId)?.name ?? ''}
        />
      );
    }
    return <>{children}</>;
  }
  if (!effectiveClientId) return <SelectClientPrompt />;

  // selectedClient is undefined while the client list loads — let the page
  // render (its queries are enabled-gated) rather than flashing a notice.
  if (module && selectedClient && !MODULE_FLAG[module](selectedClient)) {
    return <ModuleNotEnabledNotice clientName={selectedClient.name} />;
  }

  return <>{children}</>;
}

function ModuleNotEnabledNotice({ clientName }: { clientName: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Prohibit className="w-7 h-7 text-slate-400" weight="light" />
      </div>
      <h2 className="text-lg font-semibold text-slate-800 mb-1">
        {t('nav.moduleNotEnabledTitle')}
      </h2>
      <p className="text-sm text-slate-500 max-w-sm">
        {t('nav.moduleNotEnabledBody', { client: clientName })}
      </p>
    </div>
  );
}
